import { Box, keyframes, styled } from '@mui/material';

const PREFIX = 'TSMED-MainMapOverlay';
export const mainMapOverlayClasses = {
  progressPointer: `${PREFIX}-progressPointer`,
  progressLabel: `${PREFIX}-progressLabel`,
  hubs: `${PREFIX}-hubs`,
  hubsHorizontal: `${PREFIX}-hubsHorizontal`,
  leftHubs: `${PREFIX}-leftHubs`,
  topHubs: `${PREFIX}-topHubs`,
  bottomHubs: `${PREFIX}-bottomHubs`,
  rightHubs: `${PREFIX}-rightHubs`,
  flatView: `${PREFIX}-flatView`,
  flatViewSubWrapper: `${PREFIX}-flatViewSubWrapper`,
};

const dash = keyframes({
  from: { strokeDashoffset: 300 },
  to: { strokeDashoffset: 0 },
});

export const MapOverlayStyled = styled(Box, {
  shouldForwardProp: prop => prop !== 'isFlat',
})<{ isFlat: boolean }>(({ theme: { mixins, palette, spacing }, isFlat }) => ({
  ...mixins.organStyles(palette),

  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 1,

  [`.${mainMapOverlayClasses.progressPointer}`]: {
    fill: 'var(--primaryColor)',
    transformOrigin: 'center',
    path: {
      transition: 'all 0.3s ease-in-out',
      fill: 'inherit',
      transform: 'rotate(var(--heading, deg))',
      transformOrigin: 'center',
    },
  },
  [`.${mainMapOverlayClasses.hubs}`]: {
    position: isFlat ? 'relative' : 'absolute',
    transition: 'all 0.4s ease',
    zIndex: 30,

    // LEFT SIDE — vertically center
    [`&.${mainMapOverlayClasses.leftHubs}`]: {
      left: spacing(2),
      top: '50%',
      transform: 'translateY(-50%)',
    },

    // RIGHT SIDE — vertically center
    [`&.${mainMapOverlayClasses.rightHubs}`]: {
      right: spacing(2),
      top: '50%',
      transform: 'translateY(-50%)',
    },

    // TOP SIDE — horizontally center
    [`&.${mainMapOverlayClasses.topHubs}`]: {
      top: spacing(9),
      left: '50%',
      transform: 'translateX(-50%)',
    },

    // BOTTOM SIDE — horizontally center
    [`&.${mainMapOverlayClasses.bottomHubs}`]: {
      bottom: spacing(2),
      left: '65%',
      transform: 'translateX(-50%)',
    },
  },
  [`.${mainMapOverlayClasses.flatView}`]: {
    position: 'absolute',
    zIndex: 20,
    bottom: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  [`.${mainMapOverlayClasses.flatViewSubWrapper}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  [`.${mainMapOverlayClasses.hubsHorizontal}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    width: '100%',
    padding: spacing(1, 1, 0, 1),
  },

  [`.case-curve-aircraft-on-flight-link`]: {
    strokeWidth: '1px',
    strokeLinecap: 'round',
  },

  // svg base
  'svg#mainOverlaySVG': {
    touchAction:
      'none' /* Prevent native page pan/zoom on touch so D3 receives the gesture. */,
    userSelect:
      'none' /* Prevent accidental text selection while dragging (desktop + Android). */,
    WebkitUserSelect: 'none' /* iOS Safari: disable selecting SVG text/nodes */,
    WebkitTouchCallout:
      'none' /* iOS Safari: no "Copy/Share" callout on long-press */,
    WebkitTapHighlightColor:
      'transparent' /* Remove the grey/blue tap highlight on mobile for cleaner UX. */,
    overscrollBehavior:
      'contain' /* Stop scroll chaining/bounce to parent when dragging near edges. */,

    '&:active': {
      cursor: 'grabbing',
    },
  },

  // curve paths
  'svg#mainOverlaySVG path.curve': {
    strokeWidth: 1,
    stroke: '#8a8a8a',
    strokeDasharray: '5 5',
    fill: 'none',
  },
  'svg#mainOverlaySVG path.thirdPartyAviation': {
    strokeWidth: 1,
    stroke: '#8a8a8a',
    strokeDasharray: '2 2',
    fill: 'none',
  },
  'svg#mainOverlaySVG path.tmdxAviation': {
    strokeWidth: 1,
    stroke: '#8a8a8a',
    strokeDasharray: '50 2',
    fill: 'none',
  },

  // aircraft→case link lines
  'svg#mainOverlaySVG .case-curve-aircraft-on-flight-link': {
    strokeWidth: 1,
    strokeLinecap: 'round',
    vectorEffect: 'non-scaling-stroke',
    animation: `${dash} 60s linear forwards infinite`,
  },

  // hide them when projection changes
  'svg#mainOverlaySVG.projectionChanging .case-curve-aircraft-on-flight-link': {
    strokeWidth: 0,
    pointerEvents: 'none',
  },

  animation: 'dash 60s linear forwards infinite',
}));
